/**
 * Simulated `VoiceClient` (T6, docs/ARCHITECTURE.md §9-10). No media is transported and no network call is
 * made — `connect()` just resolves after a short tick, matching the server's mock adapter
 * (packages/server/src/voice/mock.ts), which only issues a credential so the client can render "Simulated
 * voice" and drive a presence heartbeat. Gameplay never depends on it.
 *
 * Speaking is entirely make-believe and stays off by default: `setSimulation` is the only way to turn it on,
 * and callers (see `useVoice.ts`) must restrict `identities` to participants that actually exist in the
 * session — presenter sessions' `simulated: true` fixtures, never a real participant's identity. In ordinary
 * (non-presenter) sessions the simulate flag stays false and this client never claims anyone is speaking.
 */
import type { VoiceClient, VoiceConnectionStatus, VoiceCredentials } from '@momentum/shared';

/** Long enough to be visibly asynchronous (a "connecting…" frame is worth showing), short enough to feel instant. */
const CONNECT_DELAY_MS = 30;
/** How often the simulated speaking pattern advances one step, when enabled. */
const SIMULATE_INTERVAL_MS = 1800;

export interface MockVoiceClient extends VoiceClient {
  readonly provider: 'mock';
  /**
   * Presenter-only control. `enabled` turns the simulated speaking pattern on or off; `identities` is the
   * roster it may pick "speakers" from. This client trusts the caller to have already filtered `identities`
   * down to participants that exist in the session (T6 §1) — it has no session knowledge of its own.
   */
  setSimulation(enabled: boolean, identities: readonly string[]): void;
}

/**
 * Pure step function for the simulated speaking pattern, exported so tests can assert its behaviour without
 * timers: odd ticks are a silent beat between speakers, even ticks name the next speaker in a round-robin.
 */
export function computeSimulatedSpeakers(identities: readonly string[], tick: number): string[] {
  if (identities.length === 0 || tick <= 0) return [];
  if (tick % 2 === 1) return [];
  const speaker = identities[(tick / 2 - 1) % identities.length];
  return [speaker];
}

export function createMockVoiceClient(): MockVoiceClient {
  let status: VoiceConnectionStatus = 'idle';
  let muted = true;
  let simulateEnabled = false;
  let simulateRoster: readonly string[] = [];
  let tick = 0;
  let intervalHandle: ReturnType<typeof setInterval> | null = null;
  let connectSeq = 0;

  const statusListeners = new Set<(status: VoiceConnectionStatus, detail?: string) => void>();
  const speakingListeners = new Set<(identities: string[]) => void>();

  function setStatus(next: VoiceConnectionStatus, detail?: string): void {
    status = next;
    for (const listener of statusListeners) listener(status, detail);
  }

  function emitSpeaking(identities: string[]): void {
    for (const listener of speakingListeners) listener(identities);
  }

  function stopSimulation(): void {
    if (intervalHandle !== null) {
      clearInterval(intervalHandle);
      intervalHandle = null;
    }
  }

  function stepSimulation(): void {
    tick += 1;
    emitSpeaking(computeSimulatedSpeakers(simulateRoster, tick));
  }

  /** (Re)starts or stops the interval so it always matches the current connected/enabled/roster state. */
  function refreshSimulation(): void {
    stopSimulation();
    tick = 0;
    if (status !== 'connected' || !simulateEnabled || simulateRoster.length === 0) {
      emitSpeaking([]);
      return;
    }
    intervalHandle = setInterval(stepSimulation, SIMULATE_INTERVAL_MS);
  }

  return {
    provider: 'mock',

    async connect(credentials: VoiceCredentials): Promise<void> {
      if (credentials.provider !== 'mock') {
        setStatus('failed', 'Mock voice client received non-mock credentials');
        return;
      }
      const seq = ++connectSeq;
      muted = true;
      setStatus('connecting');
      await new Promise<void>((resolve) => setTimeout(resolve, CONNECT_DELAY_MS));
      if (seq !== connectSeq) return; // superseded by a later connect()/disconnect() while awaiting the tick
      setStatus('connected');
      refreshSimulation();
    },

    async disconnect(): Promise<void> {
      connectSeq += 1; // invalidate any connect() still awaiting its tick
      stopSimulation();
      setStatus('disconnected');
      emitSpeaking([]);
    },

    async setMuted(next: boolean): Promise<void> {
      muted = next;
    },

    isMuted(): boolean {
      return muted;
    },

    status(): VoiceConnectionStatus {
      return status;
    },

    onSpeaking(listener) {
      speakingListeners.add(listener);
      return () => speakingListeners.delete(listener);
    },

    onStatus(listener) {
      statusListeners.add(listener);
      return () => statusListeners.delete(listener);
    },

    setSimulation(enabled: boolean, identities: readonly string[]): void {
      simulateEnabled = enabled;
      simulateRoster = identities;
      refreshSimulation();
    },
  };
}
